import { useEffect, useState } from 'react'
import { Button } from '@nazka/nazka.mapframe.components'
import { faChevronDown } from '@fortawesome/pro-light-svg-icons'

import { getWebSocketSnapshot } from '@services/websocket.service'

import './websocket-status.styl'

// This component displays the current WebSocket status in the top right of the map

const MAX_MESSAGES = 8

const SOURCE_LABELS = {
  'irideon-samples': 'Irideon',
  'mosquito-alert': 'Mosquito Alert',
  'osm': 'OpenStreetMap',
}

const SCOPE_LABELS = {
  samples: 'Samples',
  sightings: 'Sightings',
  missionData: 'Mission data',
}

function formatTimestamp(timestamp) {
  const date = timestamp ? new Date(timestamp) : new Date()
  const normalizedDate = Number.isNaN(date.getTime()) ? new Date() : date

  return normalizedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function getAppSessionCountLabel(connectedAppSessions) {
  if (typeof connectedAppSessions !== 'number') return 'Waiting for app session count'

  return `${connectedAppSessions} active app ${connectedAppSessions === 1 ? 'session' : 'sessions'}`
}

function getSourceUpdateMessage(data) {
  const scope = SCOPE_LABELS[data.scope] || data.scope || 'Data'
  const source = SOURCE_LABELS[data.source] || data.source || 'external API'
  const count = typeof data.updatedCount === 'number'
    ? ` (${data.updatedCount} ${data.updatedCount === 1 ? 'record' : 'records'})`
    : ''

  if (data.action === 'create') return `${scope} created${count}`
  if (data.action === 'delete') return `${scope} deleted${count}`

  return `${scope} fetched from ${source}${count}`
}

function WebsocketStatusControl() {
  const webSocketSnapshot = getWebSocketSnapshot()
  const [status, setStatus] = useState(() => window.websocket?.readyState === 1 ? 'connected' : 'disconnected')
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [connectedAppSessions, setConnectedAppSessions] = useState(() => webSocketSnapshot.socketStatus?.connectedSockets ?? null)
  const [lastApiFetch, setLastApiFetch] = useState(() => {
    const timestamp = webSocketSnapshot.sourceUpdate?.timestamp

    return timestamp ? formatTimestamp(timestamp) : null
  })

  useEffect(() => {
    let receivingTimeout = null

    function syncSocketStatus() {
      setStatus(window.websocket?.readyState === 1 ? 'connected' : 'disconnected')
    }

    function addMessage(message, timestamp = formatTimestamp()) {
      setMessages(prev => [
        { message, timestamp },
        ...prev,
      ].slice(0, MAX_MESSAGES))
    }

    function handleOpen() {
      setStatus('connected')
    }

    function handleClose() {
      setStatus('disconnected')
      setConnectedAppSessions(null)
    }

    function handleError() {
      setStatus('error')
    }

    function handleStatus(event) {
      const data = event.detail || {}

      if (typeof data.connectedSockets === 'number') {
        setConnectedAppSessions(data.connectedSockets)
        addMessage(getAppSessionCountLabel(data.connectedSockets), formatTimestamp(data.timestamp))
      }

      syncSocketStatus()
    }

    function handleSourceUpdated(event) {
      const data = event.detail || {}
      const timestamp = formatTimestamp(data.timestamp)

      setLastApiFetch(timestamp)
      addMessage(getSourceUpdateMessage(data), timestamp)
      setStatus('receiving')

      if (receivingTimeout) clearTimeout(receivingTimeout)
      receivingTimeout = setTimeout(syncSocketStatus, 1200)
    }

    window.addEventListener('ws:open', handleOpen)
    window.addEventListener('ws:close', handleClose)
    window.addEventListener('ws:error', handleError)
    window.addEventListener('ws:status', handleStatus)
    window.addEventListener('ws:source-updated', handleSourceUpdated)

    // Sync current state in case WebSocket already connected before this effect ran
    syncSocketStatus()

    return () => {
      if (receivingTimeout) clearTimeout(receivingTimeout)

      window.removeEventListener('ws:open', handleOpen)
      window.removeEventListener('ws:close', handleClose)
      window.removeEventListener('ws:error', handleError)
      window.removeEventListener('ws:status', handleStatus)
      window.removeEventListener('ws:source-updated', handleSourceUpdated)
    }
  }, [])

  const statusText = {
    connected: 'Socket connected',
    disconnected: 'No socket connection',
    receiving: 'New data available',
    error: 'Socket error',
  }[status] || 'Unknown'
  const socketCountLabel = status === 'disconnected' ? 'Not connected' : getAppSessionCountLabel(connectedAppSessions)
  const lastApiFetchLabel = lastApiFetch ? `Last API fetch ${lastApiFetch}` : 'No API updates yet'

  return (
    <div className={`websocket-status websocket-status--${status}${isOpen ? ' websocket-status--is-open' : ''}`}>
      <div className="websocket-status__indicator">
        <div className="websocket-status__summary">
          <span className="websocket-status__summary__state">{statusText}</span>
          <span className="websocket-status__summary__meta">{socketCountLabel}</span>
          <span className="websocket-status__summary__meta">{lastApiFetchLabel}</span>
        </div>
        {messages.length !== 0 && (
          <Button
            icon={faChevronDown}
            layout="ghost"
            className="websocket-status__indicator__toggle"
            onClick={() => setIsOpen(!isOpen)}
          />
        )}
      </div>
      <ul className="websocket-status__indicator__messages">
        {(
          messages.map((entry, idx) => (
            <li key={idx} className="websocket-status__indicator__messages__message">
              <span className="websocket-status__indicator__messages__timestamp">
                [
                {entry.timestamp}
                ]
              </span>
              {' '}
              {entry.message}
            </li>
          ))
        )}
      </ul>
    </div>
  )
}

export default WebsocketStatusControl
